/**
 * WebsiteNav — sticky top nav for customer sites.
 * Reused across all templates. Theme-aware via inline CSS vars.
 */
import { useState } from 'react';
import { Phone, Menu, X } from 'lucide-react';

export default function WebsiteNav({ config }) {
  const [open, setOpen] = useState(false);
  const phone = config.phone_display || config.phone_number || '';
  const phoneHref = config.phone_number ? `tel:${config.phone_number.replace(/\s/g, '')}` : '';

  const links = [
    { label: 'Services', href: '#services' },
    { label: 'Projects', href: '#projects' },
    { label: 'About', href: '#about' },
    { label: 'FAQ', href: '#faq' },
    { label: 'Contact', href: '#estimate' },
  ];

  return (
    <header
      className="sticky top-0 z-30 backdrop-blur-md bg-white/85 border-b border-slate-200/70"
      data-testid="ws-nav"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2.5 group" data-testid="ws-nav-logo">
          {config.logo?.wordmark_light ? (
            <img
              src={config.logo.wordmark_light}
              alt={config.business_name}
              className="h-9 lg:h-10 w-auto"
              data-testid="ws-nav-logo-img"
            />
          ) : (
            <>
              <span
                className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-black text-base shadow-sm"
                style={{ backgroundColor: config.brand?.primary || '#0f4c81' }}
              >
                {(config.business_name || 'A9').slice(0, 1)}
              </span>
              <span className="font-extrabold text-[15px] tracking-tight text-slate-900 group-hover:text-slate-700">
                {config.brand?.logo_text || config.business_name}
              </span>
            </>
          )}
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              data-testid={`ws-nav-link-${l.href.slice(1)}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {phoneHref && (
            <a
              href={phoneHref}
              className="text-sm font-semibold text-slate-700 hover:text-slate-900"
              data-testid="ws-nav-phone"
            >
              {phone}
            </a>
          )}
          <a
            href="#estimate"
            className="inline-flex items-center gap-1.5 text-white text-sm font-semibold px-4 py-2 rounded-full shadow-sm transition-transform hover:scale-[1.02]"
            style={{ backgroundColor: config.brand?.accent || config.brand?.primary || '#0f4c81' }}
            data-testid="ws-nav-cta"
          >
            Free Estimate
          </a>
        </div>

        <button
          className="lg:hidden p-2 rounded-md hover:bg-slate-100 text-slate-700"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="ws-nav-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="max-w-6xl mx-auto px-5 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-slate-700 hover:text-slate-900 py-1.5"
              >
                {l.label}
              </a>
            ))}
            {phoneHref && (
              <a
                href={phoneHref}
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700"
              >
                <Phone size={16} /> {phone}
              </a>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
